/**
 * Close-event page. Two paths:
 *
 *   - All wells complete    → POST /events/{id}/close (no force) → 200
 *                              → cached_events drops the closed event,
 *                                user routes back to /events.
 *
 *   - Some wells unsampled  → require force_reason (free-text + dropdown
 *                             of common reasons) → POST with
 *                             force_close=true → 200 with audit fields
 *                             populated server-side.
 *
 * 4xx / 5xx is enqueued via the sync engine if we're offline so the
 * close eventually reaches the server. We optimistically remove the
 * cached event row regardless — the engine retries, the next
 * /sync/me reconciles.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  AssignedEventEnvelope,
  EventCloseRequest,
  EventCloseResponse,
} from '@argos/api-types';
import { ArrowLeftIcon, AlertTriangle, Loader2, Check } from '../components/icons';
import { apiClient, HttpError } from '../api/client';
import { getDb } from '../db';
import { logEvent } from '../sync/log';
import { enqueueSubmission } from '../sync/queue';
import * as haptics from '../lib/haptics';

const COMMON_REASONS = [
  { value: 'well_dry', label: 'Well dry' },
  { value: 'access_blocked', label: 'Access blocked' },
  { value: 'equipment_failure', label: 'Equipment failure' },
  { value: 'weather', label: 'Severe weather' },
  { value: 'time_constraint', label: 'Out of time — return tomorrow' },
  { value: 'other', label: 'Other (describe below)' },
];

export function CloseEventPage() {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();

  const [envelope, setEnvelope] = useState<AssignedEventEnvelope | null>(null);
  const [missingWells, setMissingWells] = useState<string[]>([]);
  const [reasonCode, setReasonCode] = useState<string>(COMMON_REASONS[0].value);
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const db = getDb();
        const evRow = await db.cached_events.get(eventId);
        if (!evRow) {
          if (!cancelled) setError('Event not in cache');
          return;
        }
        const env = evRow.data as AssignedEventEnvelope;
        if (cancelled) return;
        setEnvelope(env);

        // Compute missing wells from cache: plan items minus complete
        // FieldRecords. Same logic the backend's /close uses.
        const planItems = env.plan_items;
        const completeIds = new Set(
          (env.existing_field_records ?? [])
            .filter((r) => r.status === 'complete')
            .map((r) => r.well_id),
        );
        // Local drafts marked synced but not yet confirmed by server
        // also count — give the tech credit for what they've submitted.
        const localSynced = await db.local_field_records
          .where('event_id')
          .equals(eventId)
          .toArray();
        for (const l of localSynced) {
          if (l.status === 'synced') completeIds.add(l.well_id);
        }
        const missing = planItems
          .filter((it) => !completeIds.has(it.well_id))
          .map((it) => it.well_name ?? it.well_id);
        if (!cancelled) setMissingWells(missing);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const requiresForce = missingWells.length > 0;
  const reasonValid = !requiresForce ||
    (reasonCode !== 'other' || reasonText.trim().length > 0);
  const composedReason =
    reasonCode === 'other'
      ? reasonText.trim()
      : `${COMMON_REASONS.find((r) => r.value === reasonCode)?.label ?? reasonCode}` +
        (reasonText.trim() ? ` — ${reasonText.trim()}` : '');

  const submit = async () => {
    if (submitting) return;
    haptics.tap();
    setSubmitting(true);
    setError(null);
    const body: EventCloseRequest = requiresForce
      ? { force_close: true, force_reason: composedReason }
      : { force_close: false };
    try {
      // Try direct call first — close is fast and we want immediate
      // feedback. On network error, fall back to enqueuing.
      const resp = await apiClient.postJson<EventCloseRequest, EventCloseResponse>(
        `/api/v1/data-vault/sampling/events/${eventId}/close`,
        body,
      );
      void logEvent('event.closed', {
        event_id: eventId,
        force_close: requiresForce,
        missing_count: resp.missing_wells?.length ?? 0,
      });
      // Drop the event from cache so the events list refreshes.
      await getDb().cached_events.delete(eventId);
      navigate('/events', { replace: true });
    } catch (err) {
      // Server-rejected (409 should NEVER happen since requiresForce
      // mirrors the server logic, but surface anyway).
      if (err instanceof HttpError && err.status >= 400 && err.status < 500) {
        setError(`Close rejected: ${stringifyBody(err.body)}`);
        setSubmitting(false);
        return;
      }
      // Network or 5xx — enqueue + optimistically remove from cache.
      try {
        await enqueueSubmission({
          endpoint: `/api/v1/data-vault/sampling/events/${eventId}/close`,
          payload: body,
          event_id: eventId,
        });
        void logEvent('event.closed.queued', {
          event_id: eventId,
          force_close: requiresForce,
        });
        await getDb().cached_events.delete(eventId);
        navigate('/events', { replace: true });
      } catch (e2) {
        setError((e2 as Error).message);
        setSubmitting(false);
      }
    }
  };

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <p className="text-accent-red mb-4">{error}</p>
        <Link to={`/events/${eventId}`} className="btn-secondary">
          Back to event
        </Link>
      </div>
    );
  }

  if (!envelope) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3 text-mono-400">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-6 gap-6">
      <Link
        to={`/events/${eventId}`}
        className="inline-flex items-center gap-1 text-mono-400 text-sm"
      >
        <ArrowLeftIcon className="h-4 w-4" /> {envelope.event.name}
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">Close event</h1>
        <p className="text-mono-400 text-sm mt-1">
          {envelope.event.site_name}
        </p>
      </div>

      {requiresForce ? (
        <>
          <div className="border border-amber-900/40 bg-amber-950/20 rounded p-3 text-sm">
            <div className="font-medium text-accent-amber mb-1">
              {missingWells.length} well{missingWells.length === 1 ? '' : 's'}{' '}
              not yet sampled
            </div>
            <ul className="text-mono-300 list-disc list-inside space-y-0.5">
              {missingWells.slice(0, 8).map((w) => (
                <li key={w}>{w}</li>
              ))}
              {missingWells.length > 8 && (
                <li className="text-mono-500">
                  …and {missingWells.length - 8} more
                </li>
              )}
            </ul>
          </div>

          <section className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-mono-500 mb-2">
                Reason
              </label>
              <div className="space-y-2">
                {COMMON_REASONS.map((r) => (
                  <label
                    key={r.value}
                    className="flex items-center gap-3 p-2 rounded border border-mono-800 cursor-pointer active:bg-mono-900"
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reasonCode === r.value}
                      onChange={() => setReasonCode(r.value)}
                      className="accent-mono-100"
                    />
                    <span className="text-mono-200">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
                Notes
                {reasonCode === 'other' && (
                  <span className="text-accent-red ml-1">(required)</span>
                )}
              </label>
              <textarea
                rows={3}
                className="input-field"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder={
                  reasonCode === 'other'
                    ? 'Describe why these wells couldn\'t be sampled.'
                    : 'Optional details — site conditions, who you spoke to, etc.'
                }
              />
            </div>
          </section>
        </>
      ) : (
        <p className="text-mono-300">
          All plan items have been sampled. Closing the event will record
          the actual end date and lock the event for editing.
        </p>
      )}

      {/* Sticky bottom action — keeps the close action visible. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-safe-4 bg-mono-950/95 backdrop-blur border-t border-mono-800">
        <button
          type="button"
          className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
          disabled={submitting || !reasonValid}
          onClick={submit}
        >
          {submitting
            ? <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.4} /> Closing…</>
            : requiresForce
              ? <><AlertTriangle className="h-4 w-4" strokeWidth={2.4} /> Close anyway</>
              : <><Check className="h-4 w-4" strokeWidth={2.4} /> Close event</>}
        </button>
        <Link
          to={`/events/${eventId}`}
          className="btn-secondary w-full mt-2 text-center"
        >
          Cancel
        </Link>
      </div>
    </div>
  );
}

function stringifyBody(b: unknown): string {
  if (b == null) return '';
  if (typeof b === 'string') return b.slice(0, 300);
  try {
    return JSON.stringify(b).slice(0, 300);
  } catch {
    return String(b);
  }
}
